import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  homepageSuggestions,
  askHomepageSuggestions,
  threads,
  savedPartiesForDraft,
} from "src/pages/home-page/utilities";
import axios from "axios";
import { ErrorLoggingV2 } from "src/utils/errorHelpers";
import { isInhouseEmail } from "src/utils/inhouseEmail";

const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;

export const getSavedParties = createAsyncThunk("homeslice/getSavedParties", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const url = `${BASE_ENDPOINT}/api/user/get-saved-parties`;
    const response = await axios.get(url, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, data: data });
  }
});

export const getAllDraftTypes = createAsyncThunk("homeslice/getAllDraftTypes", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const url = `${BASE_ENDPOINT}/api/user/get-draft-types`;
    const response = await axios.get(url, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, data: data });
  }
});

export const getEducationMetadata = createAsyncThunk("homeslice/getEducationMetadata", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const url = `${BASE_ENDPOINT}/api/user/get-education-metadata`;
    const response = await axios.get(url, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, data: data });
  }
});

export const createNewParty = createAsyncThunk("homeslice/createNewParty", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  const url = `${BASE_ENDPOINT}/api/user/update-profile`;
  delete data.has_entity;
  delete data.id;

  try {
    const response = await axios.put(url, data, { headers });
    return {
      data,
      response: response.data,
      isEdit: false,
      editId: null,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error, data });
  }
});

export const getThreadSuggestions = createAsyncThunk("homeslice/getThreadSuggestions", async (_, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    const url = `${BASE_ENDPOINT}/api/user/thread-suggestions`;
    const response = await axios.get(url, { headers });
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue({ error });
  }
});

export const onboardUser = createAsyncThunk("homeslice/onboardUser", async (data, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const headers = { Authorization: `Bearer ${accessToken}` };

  var url = `${BASE_ENDPOINT}/api/user/onboard`;

  try {
    const response = await axios.post(url, data, { headers: headers });
    return {
      data: data,
      response: response.data,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({ error: error, data: data });
  }
});

const homeSlice = createSlice({
  name: "homeslice",
  initialState: {
    // Define your initial state here
    suggestionsHomepage: homepageSuggestions,
    allSuggestions: {},
    askhomeSuggestions: askHomepageSuggestions,
    partiesList: [],
    selectedParties: {},
    selectedDocumentType: {},
    threads: threads,
    updatingParty: false,
    viewChats: false,
    showWsInputBox: false,
    savedPartiesForDraft: savedPartiesForDraft,
    email: null,
    savedParties: [],
    savedPartiesFetchStatus: "idle",
    sidenavCollapsed: false,
    draftTypesStatus: "idle",
    draftTypes: [],
    toast: {},
    onboardUserStatus: "idle",
    onboardFailureReason: "",
    isInhouseEmail: false,
    educationMetadataFetchStatus: "idle",
    smallScreenModalOpen: false,
    createNewPartyStatus: "idle",
    mobileSideNav: false,
    openLandingPageMenu: false,
    isSharedThreadPage: false,
    threadSuggestions: [],
    threadSuggestionsFetchStatus: "idle",
  },
  reducers: {
    // Define your reducers here
    addToast: (state, action) => {
      state.toast = { ...action.payload, id: Math.random() };
    },
    setSelectedDocumentType: (state, action) => {
      state.selectedDocumentType = action.payload;
    },
    setSelectedParties: (state, action) => {
      state.selectedParties = action.payload;
    },
    resetCreateNewPartyStatus: (state) => {
      state.createNewPartyStatus = "idle";
    },
    setViewChats: (state, action) => {
      if (action.payload === null || action.payload === undefined) {
        state.viewChats = !state.viewChats;
      } else {
        state.viewChats = action.payload;
      }
    },
    setWsInputBox: (state, action) => {
      state.showWsInputBox = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
      // trackAnalytics() falls back to this key for user_email; storing an
      // empty payload would write the string "undefined".
      if (action.payload) {
        localStorage.setItem("email", action.payload);
      } else {
        localStorage.removeItem("email");
      }
      // const domain = action.payload?.split("@")[1];
      state.isInhouseEmail = isInhouseEmail(action.payload);
    },
    updateSidenavCollapse: (state, action) => {
      state.sidenavCollapsed = action.payload;
    },
    setSmallScreenModalOpen: (state, action) => {
      state.smallScreenModalOpen = action.payload;
    },
    setMobileSideNav: (state, action) => {
      state.mobileSideNav = action.payload;
    },
    setOpenLandingPageMenu: (state, action) => {
      state.openLandingPageMenu = action.payload;
    },
    setIsSharedThreadPage: (state, action) => {
      state.isSharedThreadPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSavedParties.pending, (state) => {
        state.savedPartiesFetchStatus = "loading";
        state.partiesList = [];
      })
      .addCase(getSavedParties.fulfilled, (state, action) => {
        state.partiesList = action.payload.response;
        state.savedPartiesFetchStatus = "success";
      })
      .addCase(getSavedParties.rejected, (state, action) => {
        state.partiesList = [];
        state.savedPartiesFetchStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "get_saved_parties_error");
        }
      })
      // get all draft types
      .addCase(getAllDraftTypes.pending, (state) => {
        state.draftTypesStatus = "loading";
        state.draftTypes = [];
      })
      .addCase(getAllDraftTypes.fulfilled, (state, action) => {
        state.draftTypes = action.payload.response;
        state.draftTypesStatus = "success";
      })
      .addCase(getAllDraftTypes.rejected, (state, action) => {
        state.draftTypes = [];
        state.draftTypesStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "get_all_draft_types_error");
        }
      })
      // get education metadata
      .addCase(getEducationMetadata.pending, (state) => {
        state.educationMetadataFetchStatus = "loading";
        state.allSuggestions = [];
      })
      .addCase(getEducationMetadata.fulfilled, (state, action) => {
        state.allSuggestions = action.payload.response;
        state.educationMetadataFetchStatus = "success";
      })
      .addCase(getEducationMetadata.rejected, (state, action) => {
        state.allSuggestions = [];
        state.educationMetadataFetchStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "get_education_metadata_error");
        }
      })

      // get createNewParty
      .addCase(createNewParty.pending, (state) => {
        state.createNewPartyStatus = "loading";
        state.updatingParty = false;
      })
      .addCase(createNewParty.fulfilled, (state, action) => {
        var response = action.payload.data;
        response = { ...response, id: action.payload.response?.party_id };
        state.partiesList = [response, ...state.partiesList];
        state.selectedParties = response;
        state.createNewPartyStatus = "success";
        if (action.payload.editId) {
          state.updatingParty = true;
        }
      })
      .addCase(createNewParty.rejected, (state, action) => {
        state.createNewPartyStatus = "failed";
        if (action.payload?.error?.response?.status !== 401) {
          ErrorLoggingV2(action?.payload?.error, "create_new_party_error");
        }
      })

      // onboard user
      .addCase(onboardUser.pending, (state) => {
        state.onboardUserStatus = "loading";
        state.updatingParty = false;
      })
      .addCase(onboardUser.fulfilled, (state, action) => {
        var response = action.payload.data;
        response = { ...response, id: action.payload.response?.party_id };
        state.partiesList = [...state.partiesList, response];
        state.selectedParties = response;
        state.onboardUserStatus = "success";
        if (action.payload.data.id) {
          state.updatingParty = true;
        }
      })
      .addCase(onboardUser.rejected, (state, action) => {
        state.onboardUserStatus = "failed";
        state.onboardFailureReason = action.payload?.error?.response?.data?.detail;
        ErrorLoggingV2(action?.payload?.error, "onboard_user_error");
      })

      // get thread suggestions
      .addCase(getThreadSuggestions.pending, (state) => {
        state.threadSuggestionsFetchStatus = "loading";
      })
      .addCase(getThreadSuggestions.fulfilled, (state, action) => {
        state.threadSuggestions = action.payload?.suggestions || [];
        state.threadSuggestionsFetchStatus = "success";
      })
      .addCase(getThreadSuggestions.rejected, (state) => {
        state.threadSuggestions = [];
        state.threadSuggestionsFetchStatus = "failed";
      });
  },
});

export default homeSlice.reducer;
export const {
  setViewChats,
  setWsInputBox,
  setSelectedParties,
  setEmail,
  setSelectedDocumentType,
  updateSidenavCollapse,
  addToast,
  resetCreateNewPartyStatus,
  setSmallScreenModalOpen,
  setMobileSideNav,
  setOpenLandingPageMenu,
  setIsSharedThreadPage,
} = homeSlice.actions;
